import { gql } from "@apollo/client";

// Queries, constants and helpers shared by the Leave Requests screens.

export const GET_EMPLOYEES = gql`
  query GetEmployeelist($data: EmployeeFilterInput) {
    getEmployeelist(data: $data) {
      status
      employee {
        id
        fullName
      }
    }
  }
`;

// The office marks a missing document as not needed
export const WAIVE_LEAVE_DOCUMENT = gql`
  mutation WaiveLeaveDocument($leaveId: ID!, $remark: String) {
    waiveLeaveDocument(leaveId: $leaveId, remark: $remark) {
      status
      message
    }
  }
`;

export const GET_LEAVES = gql`
  query GetAdminLeaveRequests($data: AdminLeaveFilter) {
    getAdminLeaveRequests(data: $data) {
      status
      message
      total
      leaves {
        id
        leaveType
        leaveMode
        startDate
        endDate
        startTime
        endTime
        reason
        status
        autoAbsent
        adminRemark
        createdAt
        impactSnapshot
        hasDocument
        documentRequired
        documentUrl
        penaltyAmount
        projectedPenaltyAmount
        penaltyBreakdown
        employee {
          id
          fullName
          contactNumber
        }
      }
    }
  }
`;

export const UPDATE_STATUS = gql`
  mutation UpdateLeaveStatus($data: UpdateLeaveStatusInput!) {
    updateLeaveStatus(data: $data) {
      status
      message
    }
  }
`;

export const ADMIN_DIRECT_LEAVE = gql`
  mutation AdminDirectMarkLeave($data: AdminDirectLeaveInput!) {
    adminDirectMarkLeave(data: $data) {
      status
      message
    }
  }
`;

export const ADMIN_EDIT_LEAVE = gql`
  mutation AdminEditLeave($data: AdminEditLeaveInput!) {
    adminEditLeave(data: $data) {
      status
      message
    }
  }
`;

// Every mutation refreshes the list (and the summary-card query, same name)
export const REFETCH_LEAVES = ["GetAdminLeaveRequests"];

export const TIME_SLOTS = [
  "10:00:00",
  "10:30:00",
  "11:00:00",
  "11:30:00",
  "12:00:00",
  "12:30:00",
  "13:00:00",
  "13:30:00",
  "14:00:00",
  "14:30:00",
  "15:00:00",
  "15:30:00",
  "16:00:00",
  "16:30:00",
  "17:00:00",
  "17:30:00",
  "18:00:00",
  "18:30:00",
  "19:00:00",
];

// Used until the leave policies have loaded (the live list is getLeavePolicies)
export const FALLBACK_LEAVE_TYPE_OPTIONS = [
  { value: "CASUAL", label: "Casual Leave" },
  { value: "SICK", label: "Sick Leave" },
  { value: "EARNED", label: "Earned Leave" },
  { value: "OTHER", label: "Other" },
];

export const STATUS_LABELS = {
  PENDING: "Pending",
  APPROVED: "Approved Leave",
  REJECTED: "Rejected",
  ABSENT: "Unapproved Absence",
  CANCELLED: "Cancelled",
};

// Must match LEAVE_STATUS_TRANSITIONS in barbera_backend/src/utils/leave.util.js
export const STATUS_TRANSITIONS = {
  PENDING: ["APPROVED", "REJECTED", "ABSENT", "CANCELLED"],
  APPROVED: ["ABSENT", "CANCELLED"],
  ABSENT: ["APPROVED", "CANCELLED"],
  REJECTED: [],
  CANCELLED: [],
};

export const STATUS_BADGE = {
  PENDING: "bg-yellow-100 text-yellow-800",
  APPROVED: "bg-green-100 text-green-800",
  REJECTED: "bg-red-100 text-red-800",
  ABSENT: "bg-gray-800 text-white",
};

export const PAGE_SIZE = 25;
// One employee's leaves in a date range - enough for the summary cards
export const STATS_LIMIT = 200;

// Old rows and old builds used SICK_LEAVE / CASUAL_LEAVE
export const normalizeLeaveType = (type) =>
  (type || "").toUpperCase().replace(/_LEAVE$/, "") || "CASUAL";

// The leave mutations report failures as { status: "ERROR", message }
// rather than GraphQL errors, so the response has to be checked.
export const assertSuccess = (result) => {
  if (result?.status !== "SUCCESS") {
    throw new Error(result?.message || "Request failed");
  }
};

// Default times the form fills in when the leave mode changes
export const leaveModeTimes = (mode) => {
  if (mode === "HALF_DAY_FIRST")
    return { startTime: "10:00:00", endTime: "14:00:00" };
  if (mode === "HALF_DAY_SECOND")
    return { startTime: "14:30:00", endTime: "19:00:00" };
  if (mode === "FULL_DAY")
    return { startTime: "10:00:00", endTime: "19:00:00" };
  return {};
};

const DAY_MS = 1000 * 60 * 60 * 24;

const daysBetween = (start, end) => {
  const s = new Date(start);
  const e = new Date(end);
  if (isNaN(s) || isNaN(e)) return 0;
  const diff = Math.round((e - s) / DAY_MS) + 1;
  return diff > 0 ? diff : 0;
};

const customSlotDays = (startStr, endStr) => {
  if (!startStr || !endStr) return 0;
  const [h1, m1] = startStr.split(":").map(Number);
  const [h2, m2] = endStr.split(":").map(Number);
  return Math.max(0, (h2 + m2 / 60 - (h1 + m1 / 60)) / 9);
};

/** Summary cards for one employee's leaves within [startDate, endDate]. */
export const computeLeaveStats = (leaves, employeeId, startDate, endDate) => {
  const totalDays = daysBetween(startDate, endDate);
  const totals = { APPROVED: 0, PENDING: 0, REJECTED: 0, ABSENT: 0 };

  leaves.forEach((leave) => {
    if (leave.employee?.id !== employeeId) return;
    const overlap = daysBetween(
      new Date(Math.max(new Date(leave.startDate), new Date(startDate))),
      new Date(Math.min(new Date(leave.endDate), new Date(endDate))),
    );
    if (overlap <= 0 || !(leave.status in totals)) return;
    let days = overlap;
    if (
      leave.leaveMode === "HALF_DAY_FIRST" ||
      leave.leaveMode === "HALF_DAY_SECOND"
    ) {
      days = overlap * 0.5;
    } else if (leave.leaveMode === "CUSTOM_SLOT") {
      days = overlap * customSlotDays(leave.startTime, leave.endTime);
    }
    totals[leave.status] += days;
  });

  return {
    totalDays,
    approved: totals.APPROVED,
    pending: totals.PENDING,
    rejected: totals.REJECTED,
    absent: totals.ABSENT,
    workingDays: totalDays - totals.APPROVED - totals.ABSENT,
  };
};

/** CSV text from rows and [header, valueFn] columns, quoted where needed. */
export const toCsv = (rows, columns) => {
  const cell = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [
    columns.map(([header]) => cell(header)).join(","),
    ...rows.map((r) => columns.map(([, fn]) => cell(fn(r))).join(",")),
  ].join("\n");
};

export const downloadCsv = (fileName, csv) => {
  // BOM so Excel opens UTF-8 (names, ₹) correctly
  const blob = new Blob(["﻿", csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
